import { useRef, useEffect, useState, useMemo } from "react";
import { format, differenceInDays, differenceInHours, addDays, startOfDay, parseISO } from "date-fns";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, Clock, Factory, Layers } from "lucide-react";

const EnhancedGantt = ({ 
  tasks = [], 
  viewMode = "Day",
  onTaskClick,
  comparisonTasks = null,
  highlightCriticalPath = false,
}) => {
  const containerRef = useRef(null);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [hoveredTask, setHoveredTask] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    const allTasks = comparisonTasks ? [...tasks, ...comparisonTasks] : tasks;
    if (allTasks.length === 0) return;
    const dates = allTasks.flatMap(t => [new Date(t.start), new Date(t.end)]);
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    setDateRange({ start: addDays(startOfDay(minDate), -2), end: addDays(startOfDay(maxDate), 2) });
  }, [tasks, comparisonTasks]);

  // Identify critical path (longest chain)
  const criticalTaskIds = useMemo(() => {
    if (!highlightCriticalPath || tasks.length === 0) return new Set();
    const grouped = {};
    tasks.forEach(t => {
      const orderId = t.id.split('-').slice(0, 2).join('-');
      if (!grouped[orderId]) grouped[orderId] = [];
      grouped[orderId].push(t);
    });
    // Find the order with the longest total duration
    let longestOrder = '';
    let longestHours = 0;
    Object.entries(grouped).forEach(([orderId, orderTasks]) => {
      const totalH = orderTasks.reduce((sum, t) => sum + (t.data?.duration_hours || 0), 0);
      if (totalH > longestHours) { longestHours = totalH; longestOrder = orderId; }
    });
    return new Set((grouped[longestOrder] || []).map(t => t.id));
  }, [tasks, highlightCriticalPath]);

  // Collect unique work centers for legend
  const workCenters = useMemo(() => {
    const wcs = new Map();
    tasks.forEach(t => {
      if (t.resource && !wcs.has(t.resource)) {
        wcs.set(t.resource, getTaskColor(t));
      }
    });
    return Array.from(wcs.entries());
  }, [tasks]);

  if (!dateRange.start || tasks.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">No tasks to display</div>;
  }

  const totalDays = differenceInDays(dateRange.end, dateRange.start) + 1;
  const dayWidth = viewMode === "Week" ? 20 : viewMode === "Month" ? 8 : 40;
  const totalWidth = totalDays * dayWidth;

  const dateHeaders = [];
  for (let i = 0; i < totalDays; i++) {
    const date = addDays(dateRange.start, i);
    dateHeaders.push({
      date,
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      isToday: format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd'),
    });
  }

  const getTaskPosition = (task) => {
    const start = new Date(task.start);
    const end = new Date(task.end);
    const startDays = differenceInDays(start, dateRange.start);
    const duration = Math.max(differenceInDays(end, start) + 1, 0.5);
    return { left: Math.max(startDays * dayWidth, 0), width: Math.max(duration * dayWidth - 4, dayWidth / 2) };
  };

  const groupedTasks = tasks.reduce((acc, task) => {
    const orderId = task.id.split('-').slice(0, 2).join('-');
    if (!acc[orderId]) acc[orderId] = { orderId, material: task.data?.material || 'Unknown', quantity: task.data?.quantity || 0, tasks: [] };
    acc[orderId].tasks.push(task);
    return acc;
  }, {});

  const formatDT = (dateStr) => { try { return format(parseISO(dateStr), 'MMM d, yyyy HH:mm'); } catch { return dateStr; } };

  const handleTaskClick = (task) => {
    setSelectedTask(selectedTask?.id === task.id ? null : task);
    onTaskClick?.(task);
  };

  return (
    <div className="space-y-3">
      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap px-1" data-testid="gantt-legend">
        <span className="text-xs font-medium text-muted-foreground">Work Centers:</span>
        {workCenters.map(([wc, color]) => (
          <div key={wc} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm ${color}`} />
            <span className="text-xs">{wc}</span>
          </div>
        ))}
        {highlightCriticalPath && (
          <div className="flex items-center gap-1.5 ml-2 pl-2 border-l">
            <div className="w-3 h-3 rounded-sm border-2 border-red-500 bg-red-500/30" />
            <span className="text-xs font-medium text-red-600">Critical Path</span>
          </div>
        )}
      </div>

      {/* Gantt Chart */}
      <div className="gantt-enhanced-container overflow-x-auto border rounded-lg" ref={containerRef} data-testid="enhanced-gantt">
        <div style={{ minWidth: totalWidth + 220 }}>
          {/* Header */}
          <div className="flex border-b sticky top-0 bg-card z-10">
            <div className="w-52 flex-shrink-0 p-2 font-medium text-xs border-r bg-secondary/50">Order / Material</div>
            <div className="flex">
              {dateHeaders.map((dh, i) => (
                <div key={i} className={`text-center text-xs p-1 border-r ${dh.isWeekend ? 'bg-muted/50' : ''} ${dh.isToday ? 'bg-primary/10 font-bold' : ''}`} style={{ width: dayWidth }}>
                  <div className="font-medium">{format(dh.date, 'd')}</div>
                  <div className="text-muted-foreground text-[10px]">{format(dh.date, 'EEE')}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          {Object.values(groupedTasks).map((group) => (
            <div key={group.orderId} className="border-b hover:bg-secondary/10 transition-colors">
              <div className="flex">
                <div className="w-52 flex-shrink-0 p-2 border-r bg-secondary/20">
                  <div className="font-medium text-sm truncate">{group.orderId}</div>
                  <div className="text-xs text-muted-foreground truncate">{group.material} {group.quantity > 0 && `(${group.quantity})`}</div>
                </div>
                <div className="relative flex-1" style={{ height: Math.max(36, 8 + group.tasks.length * 28) }}>
                  <div className="absolute inset-0 flex">
                    {dateHeaders.map((dh, i) => (
                      <div key={i} className={`border-r ${dh.isWeekend ? 'bg-muted/20' : ''} ${dh.isToday ? 'bg-primary/5' : ''}`} style={{ width: dayWidth }} />
                    ))}
                  </div>
                  {group.tasks.map((task, taskIdx) => {
                    const pos = getTaskPosition(task);
                    const colorClass = getTaskColor(task);
                    const isCritical = criticalTaskIds.has(task.id);
                    const isSelected = selectedTask?.id === task.id;
                    return (
                      <div
                        key={task.id}
                        data-testid={`gantt-task-${task.id}`}
                        className={`absolute rounded-sm cursor-pointer transition-all ${colorClass} ${isCritical ? 'ring-2 ring-red-500 ring-offset-1' : ''} ${isSelected ? 'ring-2 ring-yellow-400 ring-offset-1 shadow-lg scale-105' : 'hover:opacity-90 hover:shadow-md'}`}
                        style={{ left: pos.left, width: pos.width, top: 4 + taskIdx * 28, height: 22 }}
                        onClick={() => handleTaskClick(task)}
                        onMouseEnter={(e) => { setHoveredTask(task); setTooltipPos({ x: e.clientX, y: e.clientY }); }}
                        onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
                        onMouseLeave={() => setHoveredTask(null)}
                      >
                        <div className="px-1.5 py-0.5 text-[10px] text-white truncate font-medium leading-tight">
                          {task.name?.replace(/^\d+:\s*/, '')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tooltip */}
        {hoveredTask && !selectedTask && (
          <div className="fixed z-50 pointer-events-none" style={{ left: tooltipPos.x + 12, top: tooltipPos.y + 12 }}>
            <Card className="p-3 shadow-lg border bg-popover text-popover-foreground min-w-[220px]">
              <div className="font-semibold text-sm mb-2">{hoveredTask.name}</div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Start:</span><span>{formatDT(hoveredTask.start)}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">End:</span><span>{formatDT(hoveredTask.end)}</span></div>
                {hoveredTask.resource && <div className="flex justify-between gap-4"><span className="text-muted-foreground">Work Center:</span><span>{hoveredTask.resource}</span></div>}
                {hoveredTask.data?.duration_hours && <div className="flex justify-between gap-4"><span className="text-muted-foreground">Duration:</span><span>{hoveredTask.data.duration_hours}h</span></div>}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Task Detail Panel */}
      {selectedTask && (
        <Card className="p-4 border-2 border-primary/30 bg-primary/5" data-testid="task-detail-panel">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-semibold text-sm">{selectedTask.name}</h4>
              <p className="text-xs text-muted-foreground">Operation Detail</p>
            </div>
            <button onClick={() => setSelectedTask(null)} className="p-1 rounded hover:bg-secondary" data-testid="close-task-detail">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs"><Clock className="w-3 h-3" /> Schedule</div>
              <p className="text-xs">{formatDT(selectedTask.start)}</p>
              <p className="text-xs">{formatDT(selectedTask.end)}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs"><Factory className="w-3 h-3" /> Work Center</div>
              <p className="text-xs font-medium">{selectedTask.resource || '-'}</p>
              {selectedTask.data?.duration_hours && <p className="text-xs">{selectedTask.data.duration_hours}h duration</p>}
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs"><Layers className="w-3 h-3" /> Material</div>
              <p className="text-xs font-medium">{selectedTask.data?.material || '-'}</p>
              <p className="text-xs">Qty: {selectedTask.data?.quantity || '-'}</p>
            </div>
            <div className="space-y-1">
              <div className="text-muted-foreground text-xs">Status</div>
              {criticalTaskIds.has(selectedTask.id) ? (
                <Badge variant="destructive" className="text-xs">Critical Path</Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">Scheduled</Badge>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

function getTaskColor(task) {
  if (task.data?.late) return "bg-red-500";
  const colors = ["bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-violet-500", "bg-pink-500", "bg-cyan-500", "bg-indigo-500", "bg-orange-500", "bg-teal-500"];
  const hash = (task.resource || task.id)?.split('').reduce((a, c) => a + c.charCodeAt(0), 0) || 0;
  return colors[hash % colors.length];
}

export default EnhancedGantt;
